import {
  Box,
  VStack,
  Heading,
  Flex,
  Button,
  HStack,
  Text,
  Switch,
  FormLabel,
  type BoxProps,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { Fragment } from 'react';
import type { League } from '@/models';
import type { leagueMember } from '@/models/user';
import { Card } from './Card';
import { LeagueButton } from './LeagueButton';
import { leagueRowButtonProps, singleLineEllipsisTextProps } from '@/components/singleLineEllipsis';
import {
  PROFILE_SECTION_BODY_SPACING,
  PROFILE_SECTION_FOOTER_BUTTON_PROPS,
  PROFILE_SECTION_HEADING_PROPS,
} from './profileSectionStyles';

interface LeagueListCardProps extends Omit<BoxProps, 'children'> {
  leagues: { [key: string]: League };
  memberData: { [key: string]: leagueMember };
  isInGlobalLeague: boolean;
  onCreateLeague: () => void;
  onJoinLeague: () => void;
  hasCompleteList: boolean;
  listVisibleInLeagues: boolean;
  listVisibilityLoading: boolean;
  onListVisibilityChange: (visible: boolean) => void;
  scrollContained?: boolean;
}

export function LeagueListCard({
  leagues,
  memberData,
  isInGlobalLeague,
  onCreateLeague,
  onJoinLeague,
  hasCompleteList,
  listVisibleInLeagues,
  listVisibilityLoading,
  onListVisibilityChange,
  scrollContained = true,
  ...cardProps
}: LeagueListCardProps) {
  const leagueKeys = Object.keys(leagues);
  const memberKeys = Object.keys(memberData);
  const footerRuleColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');

  const leagueList = (
    <Flex direction='column' width='100%' minW={0} gap={2}>
              {['global', ...leagueKeys.filter((x) => x !== 'global' && memberKeys.includes(x))].map((x) =>
                x === 'global' ? (
                  isInGlobalLeague ? (
                    memberKeys.includes('global') ? (
                      <LeagueButton
                        key={'global'}
                        leagueID='global'
                        league={leagues.global}
                        memberData={memberData.global}
                      />
                    ) : (
                      <Fragment key='global' />
                    )
                  ) : (
                    <Link key={'global'} to={'../join/GLOBAL'} style={{ display: 'block', width: '100%', minWidth: 0 }}>
                      <Button {...leagueRowButtonProps} fontSize='sm'>
                        <HStack width='full' spacing={4} align='center' minW={0} flex={1}>
                          <Text {...singleLineEllipsisTextProps} flex={1}>
                            Global League
                          </Text>
                          <Box as='em' flexShrink={0} fontSize='sm'>
                            click to join
                          </Box>
                        </HStack>
                      </Button>
                    </Link>
                  )
                ) : (
                  <LeagueButton key={x} leagueID={x} league={leagues[x]} memberData={memberData[x]} />
                ),
              )}
    </Flex>
  );

  const sectionHeading = (
    <Heading {...PROFILE_SECTION_HEADING_PROPS} flexShrink={0} textAlign='center'>
      My Leagues
    </Heading>
  );

  const footer = (
    <Box flexShrink={0} pt={3} w='full' borderTopWidth='1px' borderColor={footerRuleColor}>
      <VStack spacing={3} align='stretch' w='full'>
        <HStack spacing={3} align='center' w='full'>
          <Switch
            id='share-list-in-leagues'
            isChecked={listVisibleInLeagues}
            isDisabled={!hasCompleteList || listVisibilityLoading}
            onChange={(e) => onListVisibilityChange(e.target.checked)}
            flexShrink={0}
          />
          <FormLabel htmlFor='share-list-in-leagues' mb={0} fontSize='sm' fontWeight='medium' cursor='pointer'>
            Show List
          </FormLabel>
        </HStack>
        <HStack width='full' spacing={3} align='stretch'>
          <Button {...PROFILE_SECTION_FOOTER_BUTTON_PROPS} flex={1} onClick={onCreateLeague}>
            Create League
          </Button>
          <Button {...PROFILE_SECTION_FOOTER_BUTTON_PROPS} flex={1} onClick={onJoinLeague}>
            Join League
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  if (!scrollContained) {
    return (
      <Card w='full' {...cardProps}>
        <VStack spacing={PROFILE_SECTION_BODY_SPACING} align='stretch'>
          {sectionHeading}
          {leagueList}
          {footer}
        </VStack>
      </Card>
    );
  }

  return (
    <Card w='full' borderRadius={0} display='flex' flexDirection='column' minH={0} {...cardProps}>
      <Box flex={1} minH={0} display='flex' flexDirection='column'>
        <VStack flex={1} minH={0} spacing={PROFILE_SECTION_BODY_SPACING} align='stretch'>
          {sectionHeading}
          <Box flex={1} minH={0} minW={0} overflowY='auto' overflowX='hidden'>
            {leagueList}
          </Box>
          {footer}
        </VStack>
      </Box>
    </Card>
  );
}
